/**
 * Mock API service for testing without backend
 */

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

export interface Referral {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  zip_code: string;
  primary_diagnosis: string;
  urgency_level: string;
  status: string;
  created_at: string;
  needs: string[];
}

export interface CBO {
  id: string;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  website?: string;
  services: string[];
  zip_codes: string[];
  languages_supported: string[];
  specializations: string[];
  capacity_pct?: number;
  success_rate?: number;
  trust_score?: number;
  rating?: number;
  distance?: number;
}

export interface Metrics {
  total_referrals: number;
  total_feedback: number;
  success_rate: number;
  acceptance_rate: number;
  rejection_rate: number;
  failure_rate: number;
  top_cbos: Array<{
    cbo_id: string;
    name: string;
    success_rate: number;
  }>;
  avg_response_time_days: number;
}

class MockApiService {
  private async mockRequest<T>(data: T): Promise<ApiResponse<T>> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 100));
    return {
      success: true,
      data,
    };
  }

  async healthCheck() {
    return this.mockRequest({
      status: "healthy",
      api_version: "1.0.0",
      database: "mock",
      total_users: 2,
      total_cbos: 5,
      total_referrals: 3,
      total_feedback: 1,
    });
  }

  async getReferrals(): Promise<ApiResponse<Referral[]>> {
    const mockReferrals: Referral[] = [
      {
        id: "ref_1",
        first_name: "John",
        last_name: "Doe",
        email: "john.doe@example.com",
        zip_code: "12345",
        primary_diagnosis: "Diabetes",
        urgency_level: "Routine",
        status: "pending",
        created_at: "2025-10-03T00:00:00Z",
        needs: ["housing", "transportation"]
      },
      {
        id: "ref_2",
        first_name: "Jane",
        last_name: "Smith",
        email: "jane.smith@example.com",
        zip_code: "54321",
        primary_diagnosis: "Hypertension",
        urgency_level: "Urgent",
        status: "matched",
        created_at: "2025-10-02T00:00:00Z",
        needs: ["food_assistance", "medical_transportation"]
      }
    ];
    return this.mockRequest(mockReferrals);
  }

  async createReferral(referral: any): Promise<ApiResponse<Referral>> {
    const newReferral: Referral = {
      id: `ref_${Date.now()}`,
      first_name: referral.first_name || "New",
      last_name: referral.last_name || "Referral",
      email: referral.email || "new@example.com",
      zip_code: referral.zip_code || "00000",
      primary_diagnosis: referral.primary_diagnosis || "General",
      urgency_level: referral.urgency_level || "Routine",
      status: "pending",
      created_at: new Date().toISOString(),
      needs: referral.needs || []
    };
    return this.mockRequest(newReferral);
  }

  async getCBOs(): Promise<ApiResponse<CBO[]>> {
    const mockCBOs: CBO[] = [
      {
        id: "cbo_1",
        name: "Community Health Center",
        contact_email: "info@communityhealth.org",
        contact_phone: "(555) 123-4567",
        address: "123 Main St, City, State 12345",
        website: "https://communityhealth.org",
        services: ["housing", "transportation", "food_assistance"],
        zip_codes: ["12345", "12346", "12347"],
        languages_supported: ["English", "Spanish"],
        specializations: ["homelessness", "mental_health"],
        capacity_pct: 85,
        success_rate: 92,
        trust_score: 4.8,
        rating: 4.9,
        distance: 2.5
      },
      {
        id: "cbo_2",
        name: "Family Support Services",
        contact_email: "help@familysupport.org",
        contact_phone: "(555) 987-6543",
        address: "456 Oak Ave, City, State 12345",
        website: "https://familysupport.org",
        services: ["food_assistance", "childcare", "counseling"],
        zip_codes: ["12345", "12348", "12349"],
        languages_supported: ["English", "Spanish", "French"],
        specializations: ["family_services", "childcare"],
        capacity_pct: 70,
        success_rate: 88,
        trust_score: 4.6,
        rating: 4.7,
        distance: 5.2
      }
    ];
    return this.mockRequest(mockCBOs);
  }

  async getMetrics(): Promise<ApiResponse<Metrics>> {
    const mockMetrics: Metrics = {
      total_referrals: 3,
      total_feedback: 1,
      success_rate: 85.5,
      acceptance_rate: 78.2,
      rejection_rate: 12.3,
      failure_rate: 9.5,
      top_cbos: [
        { cbo_id: "cbo_1", name: "Community Health Center", success_rate: 92 },
        { cbo_id: "cbo_2", name: "Family Support Services", success_rate: 88 }
      ],
      avg_response_time_days: 2.3
    };
    return this.mockRequest(mockMetrics);
  }

  async register(userData: any): Promise<ApiResponse<any>> {
    return this.mockRequest({
      id: 1,
      name: userData.name,
      email: userData.email,
      role: userData.role || "user",
      is_active: true,
      created_at: new Date().toISOString()
    });
  }

  async login(loginData: any): Promise<ApiResponse<any>> {
    return this.mockRequest({
      access_token: "mock_token_123",
      token_type: "bearer",
      user: {
        id: 1,
        name: "Test User",
        email: loginData.email,
        role: "user",
        is_active: true
      }
    });
  }

  async getCurrentUser(token: string): Promise<ApiResponse<any>> {
    return this.mockRequest({
      id: 1,
      name: "Test User",
      email: "test@example.com",
      role: "user",
      is_active: true,
      created_at: "2025-10-03T00:00:00Z"
    });
  }
}

// Export singleton instance
export const apiService = new MockApiService();

// Export individual functions for convenience
export const {
  healthCheck,
  getReferrals,
  createReferral,
  getCBOs,
  getMetrics,
  register,
  login,
  getCurrentUser,
} = apiService;
